import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  PortalAuthButton,
  PortalAuthCheckbox,
  PortalAuthField,
  PortalAuthNotice,
  PortalAuthStatus
} from '../../storefront-shared/auth/PortalAuthUI';
import { useStorefrontAuth } from '../context/StorefrontAuthContext';
import '../styles/StorefrontLoginPage.css';

/**
 * Shared storefront client login UI — full page or inline/modal.
 * When `onAuthResolved` is passed (e.g. modal), successful flows call it instead of navigating.
 */
const StorefrontLoginPanel = ({
  companyData,
  companyUrl,
  returnPath,
  onAuthResolved
}) => {
  const navigate = useNavigate();
  const {
    storefrontClient,
    sessionUser,
    hasPortalAccess,
    loading,
    authNotice,
    signInWithStorefrontEmail,
    signOutStorefront
  } = useStorefrontAuth();

  const [email, setEmail] = useState(() => (
    localStorage.getItem('clientPortalRememberMe') === 'true'
      ? localStorage.getItem('clientPortalEmail') || ''
      : ''
  ));
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(() => (
    localStorage.getItem('clientPortalRememberMe') === 'true'
  ));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const signedInClient = hasPortalAccess && storefrontClient;
  const signedInButUnavailable = sessionUser && !hasPortalAccess;
  const showRegistration = companyData?.portalSettings?.allowRegistration;

  const finishReturn = () => {
    if (onAuthResolved) {
      onAuthResolved();
      return;
    }
    navigate(returnPath, { replace: true });
  };

  const handleEmailLogin = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      await signInWithStorefrontEmail({ email, password, rememberMe });
      setPassword('');
      finishReturn();
    } catch (loginError) {
      setError(loginError.message || 'We could not sign you in.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignOut = async () => {
    setSubmitting(true);
    setError('');

    try {
      await signOutStorefront();
      setPassword('');
    } catch (signOutError) {
      setError(signOutError.message || 'We could not sign you out right now.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="storefront-login-page__card">
      {signedInClient ? (
        <>
          <div className="storefront-login-page__header">
            <h1>Your account</h1>
            <p>
              You are signed in and bookings will be attached to this client portal account.
            </p>
          </div>
          <div className="storefront-login-page__summary">
            <p><strong>Name:</strong> {storefrontClient.name || storefrontClient.email}</p>
            <p><strong>Email:</strong> {storefrontClient.email}</p>
            <p><strong>Phone:</strong> {storefrontClient.phone || 'Add your phone during booking'}</p>
          </div>
          <div className="storefront-login-page__account-actions">
            <PortalAuthButton type="button" onClick={finishReturn}>
              {onAuthResolved ? 'Continue booking' : 'Return to storefront'}
            </PortalAuthButton>
            <PortalAuthButton
              type="button"
              variant="secondary"
              onClick={() => navigate(`/book/${companyUrl}/account`)}
            >
              My account
            </PortalAuthButton>
            <PortalAuthButton
              type="button"
              variant="secondary"
              onClick={handleSignOut}
              disabled={submitting}
              loading={submitting}
            >
              Sign out
            </PortalAuthButton>
          </div>
        </>
      ) : (
        <>
          <div className="storefront-login-page__header">
            <h1>Client login</h1>
            <p>
              Sign in with your client portal account to keep bookings connected to the same profile.
            </p>
          </div>

          <PortalAuthNotice>{error || authNotice}</PortalAuthNotice>

          {signedInButUnavailable ? (
            <>
              <div className="storefront-login-page__summary">
                <p>
                  <strong>{sessionUser.email}</strong> is signed in, but that account is not linked to this
                  business&apos;s client portal.
                </p>
              </div>
              <div className="storefront-login-page__account-actions">
                <PortalAuthButton
                  type="button"
                  variant="secondary"
                  onClick={handleSignOut}
                  disabled={submitting}
                >
                  Use a different account
                </PortalAuthButton>
                <PortalAuthButton type="button" onClick={finishReturn}>
                  Continue as guest
                </PortalAuthButton>
              </div>
            </>
          ) : loading ? (
            <PortalAuthStatus busy title="Checking your session" message="Loading your storefront account details." />
          ) : (
            <form onSubmit={handleEmailLogin}>
              <PortalAuthField
                id="storefront-login-email"
                name="email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="Email Address"
                autoComplete="email"
                autoFocus
                required
                startIcon="email"
              />

              <PortalAuthField
                id="storefront-login-password"
                name="password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Password"
                autoComplete="current-password"
                required
                startIcon="lock"
              />

              <div className="portal-auth-row portal-auth-row--split">
                <PortalAuthCheckbox
                  id="storefront-login-remember"
                  checked={rememberMe}
                  onChange={(event) => setRememberMe(event.target.checked)}
                  name="rememberMe"
                >
                  Remember me
                </PortalAuthCheckbox>

                <button
                  type="button"
                  className="portal-auth-link-button"
                  onClick={() => window.open('/app/forgot-password', '_blank')}
                >
                  Forgot password?
                </button>
              </div>

              <div className="portal-auth-actions-stack">
                <PortalAuthButton type="submit" loading={submitting} disabled={submitting}>
                  Sign in
                </PortalAuthButton>

                {showRegistration ? (
                  <Link
                    className="portal-auth-button portal-auth-button--secondary"
                    to={`/book/${companyData?.portalUrl || companyUrl}/register`}
                  >
                    Create account
                  </Link>
                ) : null}
              </div>
            </form>
          )}
        </>
      )}
    </section>
  );
};

export default StorefrontLoginPanel;
